import { create } from "zustand";
import { isDeletedUserError } from "../utils/authErrors";
import { SettingItem, User } from "../types";
import {
  getDomainTree,
  getUserData,
  getCustomization,
  setCustomization,
  logoutUser,
  passOnboarding,
} from "../axios/user";
import { useAuthStore } from "./authStore";
import { useStatisticsStore } from "./statisticsStore";
import { useChatStore } from "./chatStore";
import { useQuizeStore } from "./quizeStore";
import { useAssessmentStore } from "./assessmentStore";
import Cookies from "js-cookie";
import packageJson from "../../package.json";
import useAppStore from "./appStore";
import {
  DomainTree,
  getFocusDisplayName as resolveFocusDisplayName,
} from "../utils/domainFocus";
import {
  clearAuthToken,
  getAuthToken,
  hasAuthToken,
} from "../utils/authToken";
import { clearInFlightPagination } from "../utils/paginateMessages";

const USER_CACHE_KEY = "coachmate-cached-user";

const readCachedUser = (): User | null => {
  try {
    if (!hasAuthToken()) return null;

    const sessionCached = sessionStorage.getItem(USER_CACHE_KEY);
    if (sessionCached) {
      const parsed = JSON.parse(sessionCached) as User;
      return parsed?.id ? parsed : null;
    }

    const stored = localStorage.getItem(USER_CACHE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as User;
    if (!parsed?.id) return null;
    sessionStorage.setItem(USER_CACHE_KEY, stored);
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: User) => {
  try {
    if (user.id) {
      const serialized = JSON.stringify(user);
      sessionStorage.setItem(USER_CACHE_KEY, serialized);
      localStorage.setItem(USER_CACHE_KEY, serialized);
    }
  } catch {
    // ignore
  }
};

const clearCachedUser = () => {
  try {
    sessionStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore
  }
};

const initialUser: User = {
  id: "",
  is_bot: false,
  first_name: "",
  last_name: "",
  username: "",
  language_code: "",
  email: "",
  need_onboarding: true,
  is_manager: false,
};

interface UserStore {
  user: User;
  setUser: (user: User) => void;
  token: string | null;
  clearUser: () => void;
  fetchUser: () => Promise<void>;
  setToken: (token: string) => void;
  logout: () => Promise<void>;

  appVersion: string;

  domainTree: DomainTree;
  setDomainTree: (tree: DomainTree) => void;
  fetchDomainTree: () => Promise<void>;
  getFocusDisplayName: (focus: string, domain?: string | null) => string;

  setServDomainFocus: (domain: string, focus: string) => Promise<void>;

  customization: SettingItem[];
  isLoadingCustomization: boolean;
  errorCustomization: string | null;
  fetchCustomization: () => Promise<void>;
  setCustomization: (customization: SettingItem[]) => void;
  updateCustomization: (data: any) => Promise<SettingItem[]>;
  completeOnboarding: () => void;
}

let logoutInProgress: Promise<void> | null = null;

export const useUserStore = create<UserStore>((set, get) => ({
  user: readCachedUser() ?? initialUser,
  setUser: (user) => {
    writeCachedUser(user);
    set({ user });
  },
  clearUser: () => {
    clearCachedUser();
    set({ user: initialUser });
  },
  token: getAuthToken(),
  setToken: (token: string) => set({ token }),
  fetchUser: async () => {
    const token = get().token || getAuthToken();
    if (!token) {
      console.warn("No token found. Skipping fetch.");
      return;
    }
    try {
      const userData = await getUserData();

      writeCachedUser(userData);
      set({ user: userData });
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      if (isDeletedUserError(error)) {
        return;
      }
      const cached = readCachedUser();
      if (cached) {
        set({ user: cached });
      }
    }
  },
  logout: async () => {
    if (logoutInProgress) {
      return logoutInProgress;
    }

    logoutInProgress = (async () => {
    const { setAuthenticated } = useAuthStore.getState();
    const { clearStatistics } = useStatisticsStore.getState();
    const { clearChatData } = useChatStore.getState();
    const { clearStore } = useQuizeStore.getState();
    const { clearStore: clearAssessmentStore } = useAssessmentStore.getState();
    const {
      setIsAssessmentOpen,
      setIsPreassessmentOpen,
      setDeviceConflictModalOpen,
      setConnectionError,
      setCurrentSocketId,
      setIsBrowserLocked,
    } = useAppStore.getState();
    const { useMmkStore } = require("./mmkStore");
    const { clearStore: clearMmkStore } = useMmkStore.getState();
    const { disconnectSocket } = require("../utils/socket");
    const { clearPendingMessages } = require("../utils/messageOutbox");

    // Close active flows immediately to avoid UI/state mismatches during logout
    setIsAssessmentOpen(false);
    setIsPreassessmentOpen(false);
    setIsBrowserLocked(false, false);
    setDeviceConflictModalOpen(false);
    setConnectionError(null);
    setCurrentSocketId(null);

    try {
      await logoutUser();
    } catch (error) {
      console.error("Server logout failed:", error);
    }

    // Clear identity before disconnect so pending reconnect timers cannot
    // re-bind a sid with a stale cookie vs socket.auth mismatch.
    set({ user: initialUser, token: null });
    Cookies.remove("token", { path: "/" });
    clearAuthToken();
    setAuthenticated(false);
    clearInFlightPagination();

    disconnectSocket();
    clearPendingMessages();
    await clearChatData();
    await clearStatistics();
    await clearStore();
    clearAssessmentStore();
    clearMmkStore();
    sessionStorage.removeItem("controlsManualToggled");
    clearCachedUser();
    localStorage.removeItem("coachmate-app-config-cache");
    useAppStore.setState({
      focus: "",
      domain: "",
      originalFocus: null,
      originalDomain: null,
      startRecordingTrigger: 0,
      refreshChat: false,
    });
    })().finally(() => {
      logoutInProgress = null;
    });

    return logoutInProgress;
  },

  domainTree: {},
  setDomainTree: (tree) => set({ domainTree: tree }),
  fetchDomainTree: async () => {
    try {
      const treeData = await getDomainTree();
      set({ domainTree: treeData });
    } catch (error) {
      console.error("Failed to fetch domain tree:", error);
    }
  },
  getFocusDisplayName: (focus, domain) => {
    const { domainTree } = get();
    return resolveFocusDisplayName(focus, domainTree, domain);
  },

  setServDomainFocus: async (domain: string, focus: string) => {
    try {
      await useAppStore.getState().setDomainFocus(domain, focus);
    } catch (error) {
      console.error("Failed to set domain focus:", error);
      throw error;
    }
  },

  customization: [],
  isLoadingCustomization: false,
  errorCustomization: null,
  fetchCustomization: async () => {
    set({ isLoadingCustomization: true, errorCustomization: null });
    try {
      const data = await getCustomization();
      set({ customization: data, isLoadingCustomization: false });
    } catch (error) {
      set({
        errorCustomization: "Error fetching customization",
        isLoadingCustomization: false,
      });
    }
  },
  setCustomization: (customization: SettingItem[]) => set({ customization }),
  updateCustomization: async (data: any): Promise<SettingItem[]> => {
    try {
      const updatedData = await setCustomization(data);
      set({ customization: updatedData });
      return updatedData;
    } catch (error) {
      console.error("Failed to update customization:", error);
      throw error;
    }
  },

  completeOnboarding: async () => {
    try {
      await passOnboarding();
      set((state) => {
        const user = { ...state.user, need_onboarding: false };
        writeCachedUser(user);
        return { user };
      });
    } catch (error) {
      console.error("Error passing onboarding:", error);
    }
  },

  appVersion: `${packageJson.version}.${new Date().getTime()}`,
}));

export default useUserStore;
